import { useEffect, useLayoutEffect, useState } from 'react'
import {
    CanvasTexture,
    LinearFilter,
    SRGBColorSpace,
    type Texture,
} from 'three'
import { isIosSafari } from '@/src/hooks/useBreakpoint'

export type StatBadgeTextGradient = {
    /** CSS gradient angle in degrees (0deg = bottom → top). */
    angleDeg: number
    /** Gradient start color (CSS 0%). */
    from: string
    /** Gradient end color (CSS 100%). */
    to: string
}

type GradientLabelTextureResult = {
    texture: Texture
    width: number
    height: number
}

export type GradientLabelTextureOptions = {
    targetAspectRatio?: number
    textAlign?: CanvasTextAlign
}

const GRADIENT_TEXT_PIXEL_HEIGHT = 256

function applyCssLinearGradient(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    gradient: StatBadgeTextGradient,
) {
    const radians = ((gradient.angleDeg - 90) * Math.PI) / 180
    const centerX = width / 2
    const centerY = height / 2
    const length =
        Math.abs(width * Math.sin(radians)) +
        Math.abs(height * Math.cos(radians))
    const half = length / 2
    const x0 = centerX - Math.cos(radians) * half
    const y0 = centerY - Math.sin(radians) * half
    const x1 = centerX + Math.cos(radians) * half
    const y1 = centerY + Math.sin(radians) * half
    const fill = ctx.createLinearGradient(x0, y0, x1, y1)

    fill.addColorStop(0, gradient.from)
    fill.addColorStop(1, gradient.to)

    return fill
}

function buildGradientLabelTexture(
    value: string,
    gradient: StatBadgeTextGradient,
    height: number,
    fontFamily: string,
    fontWeight: number,
    italic: boolean,
    options?: GradientLabelTextureOptions,
): GradientLabelTextureResult {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
        throw new Error('Could not create 2D canvas context for stat badge text.')
    }

    const fontStyle = italic ? 'italic' : 'normal'
    const paddingX = GRADIENT_TEXT_PIXEL_HEIGHT * 0.15
    const paddingY = GRADIENT_TEXT_PIXEL_HEIGHT * 0.2
    const drawOffsetY =
        GRADIENT_TEXT_PIXEL_HEIGHT * (isIosSafari() ? 0.6 : 0.7)
    const canvasHeight = Math.ceil(GRADIENT_TEXT_PIXEL_HEIGHT + paddingY * 2)
    const baseFont = `${fontStyle} ${fontWeight} ${GRADIENT_TEXT_PIXEL_HEIGHT}px ${fontFamily}`

    ctx.font = baseFont
    const metrics = ctx.measureText(value)
    const fixedCanvasWidth = options?.targetAspectRatio
        ? Math.ceil(canvasHeight * options.targetAspectRatio)
        : null

    canvas.width = fixedCanvasWidth ?? Math.ceil(metrics.width + paddingX * 2)
    canvas.height = canvasHeight

    const availableWidth = Math.max(1, canvas.width - paddingX * 2)
    const fontScale = options?.targetAspectRatio
        ? Math.min(1, availableWidth / Math.max(metrics.width, 1))
        : 1
    const fontSize = GRADIENT_TEXT_PIXEL_HEIGHT * fontScale
    const drawFont = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`

    ctx.font = drawFont
    ctx.textBaseline = 'middle'
    // ctx.letterSpacing = '20px'
    ctx.textAlign = options?.textAlign ?? 'left'
    ctx.fillStyle = applyCssLinearGradient(
        ctx,
        canvas.width,
        canvas.height,
        gradient,
    )
    const x = ctx.textAlign === 'center' ? canvas.width / 2 : paddingX
    ctx.fillText(value, x, drawOffsetY)

    const texture = new CanvasTexture(canvas)
    texture.colorSpace = SRGBColorSpace
    texture.minFilter = LinearFilter
    texture.magFilter = LinearFilter
    const aspect = canvas.width / canvas.height
    const width = height * aspect

    return { texture, width, height }
}

export function useGradientLabelTexture(
    value: string,
    gradient: StatBadgeTextGradient,
    height: number,
    fontFamily: string,
    fontWeight: number,
    italic: boolean,
    options?: GradientLabelTextureOptions,
): GradientLabelTextureResult | null {
    const [result, setResult] = useState<GradientLabelTextureResult | null>(null)

    useLayoutEffect(() => {
        let cancelled = false
        const fontStyle = italic ? 'italic' : 'normal'
        const fontFace = `${fontStyle} ${fontWeight} ${GRADIENT_TEXT_PIXEL_HEIGHT}px ${fontFamily}`

        void document.fonts.load(fontFace).then(() => {
            if (cancelled) return

            setResult((previous) => {
                previous?.texture.dispose()
                return buildGradientLabelTexture(
                    value,
                    gradient,
                    height,
                    fontFamily,
                    fontWeight,
                    italic,
                    options,
                )
            })
        })

        return () => {
            cancelled = true
        }
    }, [
        value,
        gradient,
        height,
        fontFamily,
        fontWeight,
        italic,
        options,
    ])

    useEffect(
        () => () => {
            result?.texture.dispose()
        },
        [result],
    )

    return result
}




